import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userType, setUserType] = useState(null); // 'admin' or 'member'
    const [token, setToken] = useState(localStorage.getItem('rr_token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const storedUserType = localStorage.getItem('rr_user_type');
            setUserType(storedUserType);
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
            setUserType(response.data.user_type || localStorage.getItem('rr_user_type'));
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    // Admin login
    const loginAdmin = async (email, password) => {
        const response = await axios.post(`${API}/auth/admin/login`, { email, password });
        const { access_token, admin: adminData } = response.data;
        localStorage.setItem('rr_token', access_token);
        localStorage.setItem('rr_user_type', 'admin');
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(adminData);
        setUserType('admin');
        return adminData;
    };

    // Legacy login (for backward compatibility)
    const login = async (email, password) => {
        return loginAdmin(email, password);
    };

    // Admin register
    const registerAdmin = async (email, password, name) => {
        const response = await axios.post(`${API}/auth/admin/register`, { email, password, name });
        const { access_token, admin: adminData } = response.data;
        localStorage.setItem('rr_token', access_token);
        localStorage.setItem('rr_user_type', 'admin');
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(adminData);
        setUserType('admin');
        return adminData;
    };

    // Legacy register (for backward compatibility)
    const register = async (email, password, name) => {
        return registerAdmin(email, password, name);
    };

    // Member login
    const loginMember = async (username, password) => {
        const response = await axios.post(`${API}/auth/member/login`, { username, password });
        const { access_token, member: memberData } = response.data;
        localStorage.setItem('rr_token', access_token);
        localStorage.setItem('rr_user_type', 'member');
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(memberData);
        setUserType('member');
        return memberData;
    };

    // Member register
    const registerMember = async (memberData) => {
        const response = await axios.post(`${API}/auth/member/register`, memberData);
        const { access_token, member } = response.data;
        localStorage.setItem('rr_token', access_token);
        localStorage.setItem('rr_user_type', 'member');
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(member);
        setUserType('member');
        return member;
    };

    const logout = () => {
        localStorage.removeItem('rr_token');
        localStorage.removeItem('rr_user_type');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setUserType(null);
    };

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
    };

    // Backward compatibility - expose admin as alias
    const admin = userType === 'admin' ? user : null;
    const member = userType === 'member' ? user : null;

    return (
        <AuthContext.Provider value={{ 
            user,
            userType,
            admin,
            member,
            token, 
            loading, 
            login,
            loginAdmin,
            loginMember,
            register,
            registerAdmin,
            registerMember,
            logout,
            updateUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
